import type { ID, ISODateTime } from './ids';

/**
 * **THE ICON LIBRARY** (CONTENT_ENGINE.md §6; Evan, 2026-09-27). Icons are content: uploaded in the admin, checked,
 * cleaned up, stored as path data on a 24-unit square, and drawn by the app from a manifest it caches — so a new
 * picture ships with no app build. Two kinds, one colour each, always the ink `#1D2342` on transparent:
 *
 * - `stroke` — the Art Reference's line icons: 2-unit round strokes, no fill, three strokes at most (quiz tiles,
 *   badges);
 * - `fill` — a filled picture, the kind Recraft exports (the mission pictures): one navy shape whose holes are real
 *   holes, drawn with the non-zero rule. The upload strips the background and turns white and light fills into holes,
 *   so nothing but the ink is ever drawn — no square behind a picture on the grey locked stop.
 *
 * A key names one drawing for good (`ICON_PROMPT.md`: the subject, lowercase words joined by hyphens). An uploaded icon
 * is a DRAFT until a Publisher who did not upload it publishes it; only then is it in the manifest, and content that
 * names a draft icon cannot be published.
 */
export type QuestIconKind = 'stroke' | 'fill';
export type QuestIconStatus = 'draft' | 'live' | 'retired';

/** What the app draws: path data on a 0 0 24 24 square, in the ink. `stroke`: stroked 2, round; `fill`: filled. */
export interface QuestIconDrawing {
  kind: QuestIconKind;
  paths: string[];
}

export interface QuestIcon {
  key: string;
  /** `bundled`: compiled into the app (`@kk/ui`'s glyphs; no drawing on the wire). `uploaded`: from the admin. */
  source: 'bundled' | 'uploaded';
  status: QuestIconStatus;
  /** Uploaded icons only. */
  drawing?: QuestIconDrawing;
  /** The key this one is another name for (a rename is a new key plus an alias). */
  aliasOf?: string;
  /** Where it is used: missions (their own picture, a tile, a badge) and adventures (their badge). */
  usedBy: Array<{ kind: 'mission' | 'adventure'; id: ID; title: string; where: string }>;
  uploadedBy?: string;
  uploadedAt?: ISODateTime;
  publishedBy?: string;
  publishedAt?: ISODateTime;
}

/** The admin's icon library (O-screens `/quest/icons`): every icon, bundled and uploaded, with where each is used. */
export interface QuestIconList {
  icons: QuestIcon[];
  /** The manifest's version: it moves each time an icon is published, retired or renamed. */
  manifestVersion: number;
}

/** What the app fetches and caches: every published uploaded icon (retired ones too — they still draw). */
export interface QuestIconManifest {
  version: number;
  icons: Array<{ key: string; drawing: QuestIconDrawing; aliasOf?: string }>;
}

export type IconRule =
  | 'not-svg'
  | 'too-large'
  | 'script'
  | 'style'
  | 'image'
  | 'text'
  | 'gradient'
  | 'link'
  | 'event-handler'
  | 'transform'
  | 'element'
  | 'grid'
  | 'filled'
  | 'stroke'
  | 'stroke-width'
  | 'opacity'
  | 'colours'
  | 'background'
  | 'path-data'
  | 'empty'
  | 'strokes'
  | 'cropped'
  | 'fill-ratio';

export interface IconFinding {
  rule: IconRule;
  message: string;
}

export interface IconMetrics {
  /** Continuous pen strokes (subpaths): the stroke kind's "three at most". */
  strokes: number;
  /** The larger of the drawing's width and height, over 24. */
  fillRatio: number;
  /** The drawing's nearest distance to the frame, in units. */
  edge: number;
  bbox: [minX: number, minY: number, maxX: number, maxY: number];
}

/** The validator's answer: what it refused and why, the clean-up it drafted, and the drawing it would store. */
export interface IconCheck {
  /** Nothing refused and nothing to redraw: the clean drawing can be saved. */
  ok: boolean;
  kind?: QuestIconKind;
  errors: IconFinding[];
  /** One line per mechanical change the clean-up made, shown before saving. */
  fixes: string[];
  /** The stored file: the clean drawing in the canonical wrapper. */
  canonical?: string;
  drawing?: QuestIconDrawing;
  metrics?: IconMetrics;
}

export interface UploadQuestIconInput {
  key: string;
  svg: string;
}

/** Icon keys: the subject, lowercase words joined by hyphens (ICON_PROMPT.md), at most 32 characters. */
export const ICON_KEY = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
export const ICON_KEY_MAX = 32;

/** Whether a proposed icon key is one. */
export function isIconKey(key: string): boolean {
  return key.length <= ICON_KEY_MAX && ICON_KEY.test(key);
}
